/**
 * FayreInlinePromo — a single compact fayre card used as a contextual
 * hint inside other pages (supplier search results, supplier profile,
 * guest flow). Distinct from the homepage hero cards: this is one row,
 * tap-to-view, no message-suppliers CTA (that lives on the fayre
 * detail page itself).
 *
 * Two flavours:
 *   variant="banner"  — full-width pill, used on directory + guest flow
 *   variant="compact" — supplier-profile "see them live" line item
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarBlank, MapPin, ArrowRight, Fire } from '@phosphor-icons/react';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso; }
};

const isThisWeek = (iso) => {
  if (!iso) return false;
  try {
    const dt = new Date(iso + 'T00:00:00Z').getTime();
    return dt - Date.now() < 7 * 24 * 60 * 60 * 1000;
  } catch { return false; }
};

export default function FayreInlinePromo({ fayre, variant = 'banner', headline, testId }) {
  const navigate = useNavigate();
  if (!fayre) return null;
  const loc = fayre.location || {};
  const showWeekChip = isThisWeek(fayre.date);

  if (variant === 'compact') {
    return (
      <button
        onClick={() => navigate(`/fayres/${fayre.id}`)}
        className="w-full rounded-xl px-3 py-2.5 bg-orange-300/[0.07] hover:bg-orange-300/[0.12] border border-orange-300/20 transition-all flex items-center gap-3 text-left"
        data-testid={testId || `inline-fayre-${fayre.id}`}
      >
        <span className="text-base shrink-0">📍</span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-wider text-orange-200/80">See them live</p>
          <p className="text-sm font-black text-white truncate">{fayre.name}</p>
          <p className="text-[11px] text-white/55 truncate">{formatDate(fayre.date)} · {loc.city || loc.venue || '—'}</p>
        </div>
        <ArrowRight size={14} weight="bold" className="text-orange-200 shrink-0" />
      </button>
    );
  }

  // banner
  return (
    <button
      onClick={() => navigate(`/fayres/${fayre.id}`)}
      className="w-full rounded-2xl border border-[#B89762]/40/25 bg-gradient-to-br from-[#B89762]/15 to-[#B89762]/10 hover:from-[#B89762]/25 hover:to-[#B89762]/18 px-4 py-3 flex items-center gap-3 text-left transition-all"
      data-testid={testId || `inline-fayre-${fayre.id}`}
    >
      <div className="text-3xl shrink-0">🎪</div>
      <div className="flex-1 min-w-0">
        {headline && (
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#C29D52]/85/80 mb-0.5">{headline}</p>
        )}
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-black text-white truncate">{fayre.name}</p>
          {showWeekChip && (
            <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-orange-500/90 text-white">
              <Fire size={9} weight="fill" /> Soon
            </span>
          )}
        </div>
        <p className="text-[11px] text-white/55 mt-0.5 flex items-center gap-2">
          <span className="flex items-center gap-1"><CalendarBlank size={10} weight="bold" /> {formatDate(fayre.date)}</span>
          <span className="text-white/20">·</span>
          <span className="flex items-center gap-1 truncate"><MapPin size={10} weight="bold" /> {loc.city || loc.venue || '—'}</span>
          {fayre.supplier_count > 0 && (
            <>
              <span className="text-white/20">·</span>
              <span className="text-[#C29D52]/85">{fayre.supplier_count} supplier{fayre.supplier_count === 1 ? '' : 's'}</span>
            </>
          )}
        </p>
      </div>
      <ArrowRight size={16} weight="bold" className="text-[#C29D52]/85 shrink-0" />
    </button>
  );
}
